import {
  Box,
  Button,
  Dropdown,
  Input,
  Option,
  Popup,
  Table,
  TableRow,
  TopBar,
} from '@sam/components';
import { TableHeader } from '@sam/components/src/Table/Table.types';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CustomerArticle,
  Language,
  MailConfiguration,
  MailType,
  Order,
  ProjectCheckResult,
  ProjectProtocol,
  ProjectTimeBooking,
  ProtocolDocumentConfig,
  ProtocolMailConfig,
  SchambeckUser,
  createNewProjectProtocol,
  createNewProjectSchedule,
  downloadFile,
  generateNotification,
  getActiveCustomerArticlesForCustomer,
  getCustomerUserById,
  getLatestUserTimeBookingForProject,
  getOfferById,
  getOrderById,
  getProtocolByDateAndProjectIdAndShift,
  getProtocolForProjectAndRange,
  getProtocolPDF,
  removeIdSuffix,
  sendProtocolMail,
  updateProtocol,
  useData,
} from 'shared';
import { getCustomerLocationById } from 'shared/src/customerLocation/CustomerLocation.axios';
import {
  CustomerLocation,
  Shift,
  ShiftType,
} from 'shared/src/customerLocation/CustomerLocation.types';
import { NotificationType } from 'shared/src/notification/notification.types';
import {
  getAllProjectSchedulesForProject,
  updateProjectSchedule,
} from 'shared/src/project/Project.axios';
import { Project, ProtocolMailingSettings, Schedule } from 'shared/src/project/Project.types';
import dayjs from 'shared/src/tools/Dayjs';

import { MailPopup } from '../../components/mailPopup/MailPopup';
import { useUser } from '../../components/UserContext';
import { generateEmptyMailConfiguration } from '../../utils/mail/Mail.utils';
import {
  convertCheckResultsToTableEntries,
  convertErrorPatternsIntoTableRows,
  convertProjectTimeBookingsIntoTableEntries,
  generateDropdownOptionsForShift,
  generateEmptyProjectCheckResult,
  generateEmptyProjectTimeBooking,
  generateEmptySchedule,
  generateErrorPatternTableHeaders,
  getNextShift,
  isResultPlausible,
  shiftIsEarlier,
} from '../../utils/project/Project.utils';
import { calculateBreakTime } from '../../utils/timeTracking/TimeTracking.utils';
import { getUserNameForSimpleUser } from '../../utils/user/User.utils';
import { IconComponent, IconType } from '../../components/icons/IconComponent';
import { preventSubmitOnEnter } from '../../utils/general.utils';

export const ProjectProtocolCreate: React.FC = () => {
  const { axios, user, selectedOffices } = useUser();
  const location = useLocation<{ project: Project }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: loadedUsers } = useData('SCHAMBECK_USERS_ALL_ALL_OFFICES', {
    config: { fallbackData: [] },
  });
  const [originalProtocol, setOriginalProtocol] = useState<ProjectProtocol>();
  const [activeProtocol, setActiveProtocol] = useState<ProjectProtocol>();
  const [selectableShifts, setSelectableShifts] = useState<ShiftType[]>();
  const [selectedShift, setSelectedShift] = useState<ShiftType>();
  const [documentToShow, setDocumentToShow] = useState<Blob>();
  const dateNow: Date = new Date();
  dateNow.setDate(dateNow.getDate() - 1);
  const [selectedDate, setSelectedDate] = useState<Date>(dateNow);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [plausibleRows, setPlausibleRows] = useState<string[]>([]);
  const [order, setOrder] = useState<Order>();
  //Mailconfig of the project
  const mailingConfig: ProtocolMailingSettings | undefined =
    location.state?.project?.protocolMailing;
  const [mailConfig, setMailConfig] = useState<MailConfiguration>(
    generateEmptyMailConfiguration({
      type: MailType.PROTOCOL,
      receiver: '',
      receiverCC: mailingConfig?.receivers,
      language: Language.DE,
    }),
  );
  const [customerArticles, setCustomerArticles] = useState<CustomerArticle[]>([]);
  const [customerLocation, setCustomerLocation] = useState<CustomerLocation>();
  const [isMailPopupOpen, toggleMailPopupOpen] = useState<boolean>(false);
  const [isCurrentMonth, toggleCurrentMonth] = useState<boolean>();

  //set customerArticles to select from to create resultrows
  useEffect(() => {
    if (!activeProtocol?.customerId) return;
    getActiveCustomerArticlesForCustomer(axios, activeProtocol?.customerId).then(
      setCustomerArticles,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProtocol?.customerId]);

  // load all schedules and order for the active project
  useEffect(() => {
    if (!location.state?.project) return;
    Promise.all([
      getAllProjectSchedulesForProject(axios, location.state.project.id),
      getOrderById(axios, location.state.project.orderId),
      getOfferById(axios, location.state.project.acceptedOfferId),
    ]).then(([loadedSchedules, loadedOrder, loadedOffer]) => {
      if (!loadedOffer || !loadedOrder) return;
      setSchedules(loadedSchedules);
      setOrder(loadedOrder);
      Promise.all([
        getCustomerLocationById(axios, loadedOrder.workingLocation.customerLocationId),
        getCustomerUserById(axios, loadedOrder.customerContact),
      ]).then(([loadedCustomerLocation, loadedCustomerUser]) => {
        setCustomerLocation(loadedCustomerLocation);
        setMailConfig({
          ...mailConfig,
          language: loadedOffer.language,
          receiver: loadedCustomerUser?.contact.mail || '',
        });
      });
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.project]);

  //the active schedule for the selected date and shift
  const selectedSchedule: Schedule | undefined = useMemo((): Schedule | undefined => {
    if (!selectedShift) return;
    return schedules.find((schedule) => dayjs(selectedDate).isSame(schedule.scheduleDate, 'date'));
  }, [schedules, selectedDate, selectedShift]);

  const selectableUsers: Option[] = useMemo(() => {
    if (!selectedShift) return [];
    const scheduledUsersSelectedDay: string[] =
      activeProtocol?.timeBookings.map((booking) => removeIdSuffix(booking.userId)) || [];
    return (
      loadedUsers
        .filter(
          (user) =>
            !scheduledUsersSelectedDay.includes(user.id) &&
            !user.disabled &&
            (selectedOffices.includes(user.contractInformation.officeId) ||
              (user.contractInformation.currentOfficeId &&
                selectedOffices.includes(user.contractInformation.currentOfficeId))),
        )
        .map((user) => ({
          label: getUserNameForSimpleUser(user.id, loadedUsers),
          value: user.id,
        })) || []
    );
  }, [activeProtocol?.timeBookings, loadedUsers, selectedOffices, selectedShift]);

  /**
   * Helper method to update a booking of the active Protocol
   * @param updatedBooking upated booking to replace the existing one
   */
  const updateTimeBookings = (updatedBooking: ProjectTimeBooking, remove?: boolean): void => {
    setActiveProtocol((prev) => {
      if (!prev) return;
      return remove
        ? {
            ...prev,
            timeBookings: prev.timeBookings.filter(
              (booking) => booking.userId !== updatedBooking.userId,
            ),
          }
        : {
            ...prev,
            timeBookings: prev.timeBookings.map((booking) =>
              booking.userId === updatedBooking.userId ? updatedBooking : booking,
            ),
          };
    });
  };

  /**
   * Helper method to update the checkResults for the project
   * @param updatedResult updated result to save
   */
  const updateCheckResults = (updatedResult: ProjectCheckResult, deleteEntry?: boolean): void => {
    setActiveProtocol((prev) => {
      if (!prev) return;
      if (deleteEntry) {
        return {
          ...prev,
          checkResults: prev.checkResults.filter((result) => result.id !== updatedResult.id),
        };
      }
      return {
        ...prev,
        checkResults: prev.checkResults.map((result) =>
          result.id === updatedResult.id ? updatedResult : result,
        ),
      };
    });
  };

  /**
   * Util method to copy the time data of one booking to another
   * @param booking the booking that should be copied
   */
  const copyBookingTimes = (booking: ProjectTimeBooking): void => {
    if (!activeProtocol) return;
    const updatedBookings: ProjectTimeBooking[] = activeProtocol?.timeBookings.map((entry) => ({
      ...entry,
      breakTime: booking.breakTime,
      breakTimeUser: booking.breakTimeUser,
      endTime: booking.endTime,
      startTime: booking.startTime,
      userDrivingTimeArrival: booking.userDrivingTimeArrival,
      userDrivingTimeDeparture: booking.userDrivingTimeDeparture,
      customerDrivingTimeArrival: booking.customerDrivingTimeArrival,
      customerDrivingTimeDeparture: booking.customerDrivingTimeDeparture,
    }));
    setActiveProtocol({ ...activeProtocol, timeBookings: updatedBookings });
  };

  /**
   * Util method to add a new entry for an already present user
   * @param userId the id of the user to add a new booking for
   */
  const addUserEntry = useCallback(
    (userId: string): void => {
      if (!activeProtocol) return;
      const nameSuffix: string =
        '_copy_' +
        (activeProtocol.timeBookings.filter((booking) => booking.userId.includes(userId)).length +
          1);
      setActiveProtocol({
        ...activeProtocol,
        timeBookings: [
          ...activeProtocol.timeBookings,
          generateEmptyProjectTimeBooking({ userId: userId + nameSuffix }),
        ],
      });
    },
    [activeProtocol],
  );

  // Hook to keep userRows up to date
  const userRows: TableRow[] = useMemo((): TableRow[] => {
    if (!activeProtocol) return [];
    return convertProjectTimeBookingsIntoTableEntries(
      activeProtocol.timeBookings,
      loadedUsers,
      updateTimeBookings,
      copyBookingTimes,
      addUserEntry,
    );
  }, [activeProtocol, addUserEntry, loadedUsers]);

  //Hook to keep rows for the existing errorPatterns for the project up to date
  const patternRows: TableRow[] = useMemo(() => {
    if (!location.state?.project?.errorPatterns) return [];
    return convertErrorPatternsIntoTableRows(location.state.project.errorPatterns, undefined, true);
  }, [location.state?.project?.errorPatterns]);

  //useCallback function to check if a checkReult is plausible or not
  const checkPlausibleResult = useCallback(
    (resultId: string): void => {
      if (!activeProtocol) return;
      const result: ProjectCheckResult | undefined = activeProtocol.checkResults.find(
        (resultToCheck) => resultToCheck.id === resultId,
      );
      if (result) {
        const isPlausible: boolean = isResultPlausible(result);
        if (isPlausible) {
          if (!plausibleRows.includes(resultId)) {
            setPlausibleRows((prev) => [...prev, resultId]);
          }
        } else {
          setPlausibleRows((prev) => prev.filter((row) => row !== resultId));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeProtocol],
  );

  //Hook to keep rows for the results up to date
  const resultRows: TableRow[] = useMemo((): TableRow[] => {
    if (!activeProtocol) return [];
    const plannedUserIds: string[] = activeProtocol.timeBookings.map((booking) => booking.userId);
    const plannedUsers: SchambeckUser[] = loadedUsers.filter((user) =>
      plannedUserIds.includes(user.id),
    );
    const rows: TableRow[] = convertCheckResultsToTableEntries(
      activeProtocol.checkResults,
      updateCheckResults,
      location.state?.project?.errorPatterns || [],
      customerArticles,
      checkPlausibleResult,
      plannedUsers,
    );
    const updatedRows: TableRow[] = rows.map((row) =>
      plausibleRows.includes(row.id) ? row : { ...row, background: 'yellow' },
    );
    return updatedRows;
  }, [
    activeProtocol,
    loadedUsers,
    location.state?.project?.errorPatterns,
    customerArticles,
    checkPlausibleResult,
    plausibleRows,
  ]);

  //Check the results after each selection of a different protocol and initially set plausibility
  useEffect(() => {
    if (!activeProtocol) return;
    activeProtocol.checkResults.forEach((result) => checkPlausibleResult(result.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProtocol?.id, checkPlausibleResult]);

  //Hook to get the correct protocol after each change of the date or shift
  useEffect(() => {
    if (!location.state?.project?.id) return;
    if (!selectedShift)
      getOrderById(axios, location.state.project.orderId).then((loadedOrder) => {
        if (!loadedOrder) return;
        const orderShifts: ShiftType[] = loadedOrder?.shifts.map((shift) => shift.type);
        setSelectableShifts(orderShifts);
        setSelectedShift(orderShifts[0]);
      });
    else
      getProtocolByDateAndProjectIdAndShift(
        axios,
        location.state.project.id,
        selectedDate,
        selectedShift,
      ).then((loadedProtocol) => {
        setActiveProtocol(loadedProtocol);
        setOriginalProtocol(loadedProtocol);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.project, selectedDate, selectedShift]);

  /**
   * Submit handler that starts the update on the database
   * @param evt FormEvent to prevent default behaviour
   */
  const handleSubmit = (evt: FormEvent): void => {
    evt.preventDefault();
    if (!activeProtocol) return;
    if (selectedSchedule)
      selectedSchedule.id
        ? updateProjectSchedule(axios, selectedSchedule)
        : createNewProjectSchedule(axios, selectedSchedule);
    updateProtocol(axios, activeProtocol).then((success) => success && navigate(-1));
  };

  //Helper metohd to add a new projectCheckResult
  const handleAddCheckResult = (): void => {
    const generatedCheckResult = generateEmptyProjectCheckResult();
    if (activeProtocol)
      setActiveProtocol({
        ...activeProtocol,
        checkResults: [...activeProtocol.checkResults, generatedCheckResult],
      });
  };

  /**
   * Helper method to create a new Schedule and Protocol
   * @param userId id of the user to add to the created protocol
   */
  const createScheduleAndProtocol = (userId: string): void => {
    if (!selectedShift || !location.state?.project) return;
    const scheduledUser: Map<ShiftType, string[]> = new Map<ShiftType, string[]>();
    scheduledUser.set(selectedShift, [userId]);
    const createdSchedule: Schedule = generateEmptySchedule({
      orderId: location.state.project.orderId,
      referenceId: location.state.project.id,
      scheduledUsers: scheduledUser,
      scheduleDate: selectedDate,
    });
    if (createdSchedule && location.state?.project) {
      createNewProjectProtocol(
        axios,
        selectedShift,
        location.state.project.id,
        selectedDate,
        createdSchedule,
      ).then((createdProtocol) => {
        if (!createdProtocol) return;
        setActiveProtocol(createdProtocol);
        setOriginalProtocol(createdProtocol);
        setSchedules([...schedules, createdSchedule]);
      });
    }
  };

  /**
   * Util method to add a timeBooking to a project and schedule
   * @param userId id of the user to add
   */
  const addTimeBookingToProject = (userId: string): void => {
    if (!selectedShift || !activeProtocol || !selectedSchedule) return;
    const scheduleToUpdate: Map<ShiftType, string[]> | undefined = selectedSchedule.scheduledUsers;
    const updatedUsers: string[] = [...(scheduleToUpdate.get(selectedShift) || []), userId];
    if (!scheduleToUpdate) return;
    scheduleToUpdate.set(selectedShift, updatedUsers);
    setSchedules(
      schedules.map((schedule) =>
        schedule.id === selectedSchedule.id
          ? { ...schedule, scheduledUsers: scheduleToUpdate }
          : schedule,
      ),
    );

    const shift: Shift | undefined = order?.shifts.find((shift) => shift.type === selectedShift);
    let newBooking: ProjectTimeBooking = generateEmptyProjectTimeBooking({
      userId,
      startTime: shift?.startTime || 0,
      endTime: shift?.endTime || 0,
      breakTime: calculateBreakTime(
        selectedSchedule.appointmentDetail?.startTime || 0,
        selectedSchedule.appointmentDetail?.endTime,
      ),
    });
    const activeShift: Shift | undefined = order?.shifts?.find(
      (shift) => shift.type === selectedShift,
    );
    const drivingTimeArrival: number =
      customerLocation?.officeDrivingTimesArrival.get(activeProtocol.officeId) || 0;
    const drivingTimeDeparture: number =
      customerLocation?.officeDrivingTimesDeparture.get(activeProtocol.officeId) || 0;

    newBooking.customerDrivingTimeArrival = drivingTimeArrival;
    newBooking.customerDrivingTimeDeparture = drivingTimeDeparture;
    getLatestUserTimeBookingForProject(axios, activeProtocol.projectId, userId).then(
      (loadedTimeBooking) => {
        if (!loadedTimeBooking || !activeShift) return;
        newBooking = {
          ...newBooking,
          customerArrival: loadedTimeBooking.customerArrival,
          customerDeparture: loadedTimeBooking.customerArrival,
          customerDrivingTimeArrival: loadedTimeBooking.customerDrivingTimeArrival,
          customerDrivingTimeDeparture: loadedTimeBooking.customerDrivingTimeDeparture,
          userDrivingTimeArrival: loadedTimeBooking.userDrivingTimeArrival,
          userDrivingTimeDeparture: loadedTimeBooking.userDrivingTimeDeparture,
          startTime: loadedTimeBooking.startTime,
          endTime: loadedTimeBooking.endTime,
        };
        setActiveProtocol({
          ...activeProtocol,
          timeBookings: [...activeProtocol.timeBookings, newBooking],
        });
        return;
      },
    );
    setActiveProtocol({
      ...activeProtocol,
      timeBookings: [...activeProtocol.timeBookings, newBooking],
    });
  };

  /**
   * Helper method to add a user to the active schedule and adds an empty timeBookings for display
   * @param userId id of the user to add
   */
  const addUserToSchedule = (userId: string): void => {
    if (!selectedShift || !location.state?.project) return;
    if (!selectedSchedule || !activeProtocol) {
      createScheduleAndProtocol(userId);
    } else {
      addTimeBookingToProject(userId);
    }
  };

  /**
   * Helper method to get the protocol document and open it in a popup
   */
  const openDocumentPreview = async (lastMonth?: boolean): Promise<void> => {
    if (!location.state?.project) return;
    if (lastMonth) {
      const startDate: Date = new Date();
      startDate.setMonth(startDate.getMonth() - 1);
      startDate.setDate(1);
      const endDate: dayjs.Dayjs = dayjs(startDate).endOf('month');
      getProtocolForProjectAndRange(axios, location.state.project.id, {
        startDate,
        endDate: endDate.toDate(),
      }).then(setDocumentToShow);
      return;
    } else return getProtocolPDF(axios, location.state.project.id).then(setDocumentToShow);
  };

  /**
   * Helper method to download the document as pdf
   */
  const downloadProtocol = async (): Promise<void> => {
    if (!location.state?.project) return;
    const document: Blob | undefined = await getProtocolPDF(axios, location.state.project.id);
    if (document)
      downloadFile(
        document,
        t('pages.project.protocol.fileName', {
          replace: { number: location.state.project.numberRangeNumber || '' },
        }),
      );
  };

  /**
   * Helper method to update the times or results without updating the whole object, without loosing local changes
   * @param type decides if the times or the results should be updated
   */
  const updateProjectPart = (type: 'times' | 'results'): void => {
    if (!activeProtocol || !originalProtocol)
      return generateNotification({
        type: NotificationType.ERROR,
        value: t('general.notification.error.updateProtocol'),
      });

    //Keep schedules in sync with timeBookings, so we update or create an schedule if we update the timeBookings
    if (selectedSchedule && type === 'times')
      selectedSchedule.id
        ? updateProjectSchedule(axios, selectedSchedule)
        : createNewProjectSchedule(axios, selectedSchedule);

    const editedProtocol: ProjectProtocol =
      type === 'times'
        ? { ...activeProtocol, checkResults: originalProtocol.checkResults }
        : { ...activeProtocol, timeBookings: originalProtocol.timeBookings };

    updateProtocol(axios, editedProtocol).then((updatedProtocol) => {
      if (updatedProtocol) {
        setActiveProtocol(
          type === 'times'
            ? { ...updatedProtocol, checkResults: activeProtocol.checkResults }
            : { ...updatedProtocol, timeBookings: activeProtocol.timeBookings },
        );
        setOriginalProtocol(updatedProtocol);

        generateNotification({
          type: NotificationType.SUCCESS,
          value:
            type === 'times'
              ? t('general.notification.success.updateProtocolTimes')
              : t('general.notification.success.updateProtocolResults'),
        });
      }
    });
  };

  /**
   * Method to handle click on the chevron to get to the next day or shift
   */
  const handleNextClick = (): void => {
    if (!selectedShift || !selectableShifts) return;

    const nextShift: ShiftType = getNextShift(selectedShift, selectableShifts, true);
    if (selectableShifts?.length === 1 || shiftIsEarlier(selectedShift, nextShift)) {
      const nextDate = new Date(selectedDate);
      nextDate.setDate(nextDate.getDate() + 1);
      setSelectedDate(nextDate);
    }
    setSelectedShift(nextShift);
  };
  /**
   *
   * Util method to handle click to the previous shift
   * @returns
   */
  const handlePrevClick = (): void => {
    if (!selectedShift || !selectableShifts) return;
    const prevShift: ShiftType = getNextShift(selectedShift, selectableShifts, true);
    if (selectableShifts.length === 1 || !shiftIsEarlier(prevShift, selectedShift)) {
      const nextDate = new Date(selectedDate);
      nextDate.setDate(nextDate.getDate() - 1);
      setSelectedDate(nextDate);
    }
    setSelectedShift(prevShift);
  };

  /**
   * Helper method to save the protocol if the mailpopup gets opened
   * @returns void Promise
   */
  const handleOpenMailPopup = (currMonth: boolean): Promise<void> => {
    if (!activeProtocol) return Promise.resolve();
    toggleMailPopupOpen(true);
    if (selectedSchedule) {
      toggleCurrentMonth(currMonth);
      selectedSchedule.id
        ? updateProjectSchedule(axios, selectedSchedule)
        : createNewProjectSchedule(axios, selectedSchedule);
      return updateProtocol(axios, activeProtocol).then(setActiveProtocol);
    }
    return Promise.resolve();
  };

  /**
   * Helper method to send the protocol per mail
   * @returns Promise to detect when the promise is resolved
   */
  const handleSendMail = async (): Promise<void> => {
    if (!activeProtocol) return Promise.resolve();
    const config: ProtocolMailConfig = {
      ...mailConfig,
      activeUserId: user.id,
      protocolId: activeProtocol.id,
      currMonth: isCurrentMonth,
    };
    return sendProtocolMail(axios, config).then(() => toggleMailPopupOpen(false));
  };
  return (
    <form onSubmit={handleSubmit} onKeyDown={preventSubmitOnEnter}>
      <TopBar
        title={t('pages.project.protocol.topBarHeadline', {
          replace: { project: location.state?.project?.numberRangeNumber },
        })}
        onBackClick={() => navigate(-1)}
      >
        <Button
          value={t('general.buttons.documentPreviewLastMonth')}
          onClick={() => openDocumentPreview(true)}
        />
        {location.state?.project?.protocolMailing?.setting !== ProtocolDocumentConfig.NONE && (
          <>
            <Button
              value={t('general.buttons.sendPrevious')}
              onClick={() => handleOpenMailPopup(false)}
            />
            <Button value={t('general.buttons.send')} onClick={() => handleOpenMailPopup(true)} />
          </>
        )}
        <Button value={t('general.buttons.documentPreview')} onClick={openDocumentPreview} />
        <Button value={t('general.buttons.download')} type="button" onClick={downloadProtocol} />
        <Button value={t('general.buttons.save')} type="submit" />
      </TopBar>
      {documentToShow && (
        <Popup isOpen={!!documentToShow} onClose={() => setDocumentToShow(undefined)}>
          <object
            data={URL.createObjectURL(documentToShow)}
            type={documentToShow.type}
            width="1000px"
            height="700px"
          />
        </Popup>
      )}
      {activeProtocol?.id && (
        <MailPopup
          isOpen={isMailPopupOpen}
          mailConfig={mailConfig}
          userId={user.id}
          sendMail={handleSendMail}
          updateConfig={setMailConfig}
          toggleOpen={toggleMailPopupOpen}
          referenceId={activeProtocol.id}
        />
      )}
      <div className="project-protocol__input-wrapper">
        <div className="project-protocol__input-wrapper__date-wrapper">
          <IconComponent type={IconType.ARROW_LEFT} width={25} onClick={handlePrevClick} />
          <Input
            maxWidth={300}
            label={t('pages.project.protocol.date')}
            value={selectedDate}
            type="date"
            onChangeDate={(date) => date && setSelectedDate(date)}
          />
          <IconComponent type={IconType.ARROW_RIGHT} width={25} onClick={handleNextClick} />
        </div>
        <Dropdown
          onChange={(shift) => setSelectedShift(shift as ShiftType)}
          label={t('pages.project.protocol.shift')}
          options={generateDropdownOptionsForShift(selectableShifts)}
          selectedOption={selectedShift}
        />
      </div>
      <Box
        title={
          <div className="user-create__box-title-wrapper">
            <p className="box__title">{t('pages.project.protocol.timetable')}</p>
            <div className="project-protocol__dropdown-wrapper">
              <Dropdown
                label={t('pages.project.protocol.selectUser')}
                options={selectableUsers}
                onChange={(userId) => addUserToSchedule(userId)}
              />
              <Button
                value={t('general.buttons.save')}
                onClick={() => updateProjectPart('times')}
              />
            </div>
          </div>
        }
      >
        <Table
          rows={userRows}
          header={
            t('pages.project.protocol.timesTableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
      <Box title={t('pages.project.protocol.errorPatterns')}>
        <Table
          rows={patternRows}
          header={
            t('pages.project.protocol.patternHeaders', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
      <Box
        title={
          <div className="user-create__box-title-wrapper">
            <p className="box__title">{t('pages.project.protocol.checkResults')}</p>
            <div className="project-protocol__dropdown-wrapper">
              <IconComponent
                type={IconType.ADD}
                onClick={handleAddCheckResult}
                className="user-create__box-title-wrapper__icon"
              />
              <Button
                value={t('general.buttons.save')}
                onClick={() => updateProjectPart('results')}
              />
            </div>
          </div>
        }
      >
        <Table
          paginationConfig={{ pageSize: resultRows.length }}
          selectAllLabel={t('pages.accounting.overview.selectAll')}
          rows={resultRows}
          header={generateErrorPatternTableHeaders(location.state?.project?.errorPatterns || [])}
        />
      </Box>
    </form>
  );
};
